import type {
  AnnualWeekdayKey,
  Grade,
  ProgramProgressCell,
} from '../types/domain';
import {
  buildDashboardAnnualDaySlots,
  buildDashboardAnnualDaySlotsFromLoadedData,
  type DashboardAnnualDaySlot,
  type DashboardLessonSectionBundle,
  type DashboardLessonSectionTermData,
} from './dashboardAnnualDaySlots';
import { annualSlotHasTimetableProgramAssignment } from './annualProgressSlotSource';
import { dateToWeekdayKey, mondayIsoForDateInLocal } from './dashboardDateContext';
import { toISODate } from './date';
import { resolveTermNumberForViewDate, inferAcademicYearForViewDate } from './dashboardLessonSectionResolve';
import {
  findAnnualTimetableCellsCacheOrQueryForGrade,
  readAnnualProgressMetaCacheOrQueryWithFallback,
  readProgramFixedRowsCacheOrQuery,
  readProgramScopedRowsCacheOrQuery,
} from '../query/readSchoolQueryCache';
import { queryClient } from '../query/queryClient';
import { schoolKeys } from '../query/keys';
import { loadProgramProgressCellsCache } from './programProgressCellsCache';
import {
  loadClassScheduleSettingsCache,
  loadGradeTrackSectionsCache,
} from '../services/sectionLayoutService';
import { loadAnnualProjectPrograms } from './annualProjectProgramsStorage';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import type { ProgramContentSlidePayloadV1 } from './programContentSlideStorage';
import { localStorageSetItemWithQuotaRecovery } from './localStorageQuota';
import { applyProgramContentSlideLearningOverlayToSlots } from './programContentSlideLearningOverlay';
import { withAcademicCalendarOffDaysFromLocalCache } from './dashboardAcademicHolidays';

const SNAPSHOT_VERSION = 'v4';
const SNAPSHOT_PREFIX = `cp.dashboardLessonDaySlots.${SNAPSHOT_VERSION}`;
const LEGACY_SNAPSHOT_PREFIXES = ['cp.dashboardLessonDaySlots.v2.', 'cp.dashboardLessonDaySlots.v3.'];
const CHANNEL_NAME = 'cp-dashboard-lesson-day-slots-v4';
const REQUEST_CHANNEL_NAME = 'cp-dashboard-lesson-day-slots-request-v4';
/** workspace+grade당 날짜별 스냅샷 상한(latest 제외). 넘치면 오래된 것부터 삭제 */
const MAX_DATE_SNAPSHOTS_PER_GRADE = 14;

const isDevSnapshotLog = process.env.NODE_ENV === 'development';

function devSnapshotInfo(...args: unknown[]): void {
  if (!isDevSnapshotLog) return;
  console.info(...args);
}

function devSnapshotWarn(...args: unknown[]): void {
  if (!isDevSnapshotLog) return;
  console.warn(...args);
}

/** 메인 프레젠에 필요한 필드만 (직렬화 실패·용량 문제 방지) */
export type PresentationTimetableSlotSnapshot = {
  slotIndex: number;
  periodLabel: string;
  templateLabel: string;
  timetableText: string;
  timetableColorHex: string;
  timetableSlotKind?: DashboardAnnualDaySlot['timetableSlotKind'];
  calendarOffDayKind?: DashboardAnnualDaySlot['calendarOffDayKind'];
  academicYear: number;
  termNumber: 1 | 2;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  dateIso: string;
  annualCellKey: string;
  slideOwner: ProgramContentSlideOwnerRef | null;
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
  slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
  slideWorkspaceScopeId: string;
  slideLearningTextRaw: string;
  slideCreatePresentationRefOnSave: ProgramContentSlidePayloadV1['createPresentationRefOnSave'] | undefined;
  display: DashboardAnnualDaySlot['display'];
};

export type DashboardLessonDaySlotsSnapshotV2 = {
  v: 2;
  workspaceId: string;
  gradeId: string;
  dateIso: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
  academicYear: number;
  activeTermNumber: 1 | 2 | null;
  daySlots: PresentationTimetableSlotSnapshot[];
  savedAt: number;
};

type SnapshotRequestMessage = {
  type: 'request';
  workspaceId: string;
  gradeId: string;
  dateIso: string;
};

function dateKey(workspaceId: string, gradeId: string, dateIso: string): string {
  return [SNAPSHOT_PREFIX, workspaceId.trim(), gradeId.trim(), dateIso.trim()].join('.');
}

function latestKey(workspaceId: string, gradeId: string): string {
  return [SNAPSHOT_PREFIX, workspaceId.trim(), gradeId.trim(), 'latest'].join('.');
}

const DATE_ISO_SUFFIX_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 전체 origin의 날짜별 스냅샷을 workspace+grade 그룹별로 MAX개만 남긴다.
 * (날짜를 넘길 때마다 키가 쌓여 QuotaExceeded가 나는 것을 막음)
 */
function pruneOldDateSnapshots(
  workspaceId: string,
  gradeId: string,
  keepDateIso: string
): void {
  if (typeof window === 'undefined') return;
  const preferWs = workspaceId.trim();
  const preferGid = gradeId.trim();
  const preferDay = keepDateIso.trim();
  const preferGroup = preferWs && preferGid ? `${preferWs}.${preferGid}` : '';

  type Entry = { key: string; dateIso: string; savedAt: number };
  const byGroup = new Map<string, Entry[]>();

  try {
    const legacyKeys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && LEGACY_SNAPSHOT_PREFIXES.some((p) => key.startsWith(p))) legacyKeys.push(key);
    }
    legacyKeys.forEach((key) => window.localStorage.removeItem(key));

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(`${SNAPSHOT_PREFIX}.`)) continue;
      const rest = key.slice(SNAPSHOT_PREFIX.length + 1);
      const parts = rest.split('.');
      if (parts.length !== 3) continue;
      const [ws, gid, dateIso] = parts;
      if (!ws || !gid || dateIso === 'latest' || !DATE_ISO_SUFFIX_RE.test(dateIso)) continue;
      const group = `${ws}.${gid}`;
      const snap = parseSnapshot(window.localStorage.getItem(key));
      const list = byGroup.get(group) ?? [];
      list.push({ key, dateIso, savedAt: snap?.savedAt ?? 0 });
      byGroup.set(group, list);
    }
  } catch {
    return;
  }

  Array.from(byGroup.entries()).forEach(([group, entries]) => {
    if (entries.length <= MAX_DATE_SNAPSHOTS_PER_GRADE) return;

    entries.sort((a: Entry, b: Entry) => {
      if (a.savedAt !== b.savedAt) return b.savedAt - a.savedAt;
      return b.dateIso.localeCompare(a.dateIso);
    });

    const keep = new Set<string>();
    if (group === preferGroup && preferDay) keep.add(preferDay);
    for (const entry of entries) {
      if (keep.size >= MAX_DATE_SNAPSHOTS_PER_GRADE) break;
      keep.add(entry.dateIso);
    }

    for (const entry of entries) {
      if (keep.has(entry.dateIso)) continue;
      try {
        window.localStorage.removeItem(entry.key);
      } catch {
        /* ignore */
      }
    }
  });
}

function emptyTermData(termNumber: 1 | 2): DashboardLessonSectionTermData {
  return {
    termNumber,
    annualRows: [],
    progressRows: [],
    scopedRows: [],
    fixedRows: [],
    holidayDays: [],
    offDayByDate: {},
    dayKindByDate: {},
  };
}

export function slimDashboardDaySlotsForPresentation(
  slots: DashboardAnnualDaySlot[],
  workspaceId?: string
): PresentationTimetableSlotSnapshot[] {
  const merged = applyProgramContentSlideLearningOverlayToSlots(slots, workspaceId ?? '');
  return merged.map((s) => {
    const d = s.display;
    /* display는 문자열 필드만 복사 — 순환참조·비직렬화 값 차단 */
    const display: DashboardAnnualDaySlot['display'] = {
      progressUnit: d?.progressUnit ?? '',
      progressUnitNote: d?.progressUnitNote ?? '',
      progressLearning: d?.progressLearning ?? '',
      progressLearningNote: d?.progressLearningNote ?? '',
      progressPage: d?.progressPage ?? '',
      progressAssistantPage: d?.progressAssistantPage ?? '',
      progressCurrentPeriod: d?.progressCurrentPeriod ?? '',
      progressTotalPeriod: d?.progressTotalPeriod ?? '',
      progressMaterial: d?.progressMaterial ?? '',
      progressBgByField: {},
      progressCross: d?.progressCross ?? '',
      progressCrossNote: d?.progressCrossNote ?? '',
      progressMemoPrimary: d?.progressMemoPrimary ?? '',
      progressMemoNote: d?.progressMemoNote ?? '',
      progressEval: d?.progressEval ?? '',
      progressEvalCode: d?.progressEvalCode ?? '',
      progressEvalNote: d?.progressEvalNote ?? '',
      progressClassRecord: d?.progressClassRecord ?? '',
      progressClassRecordKind: d?.progressClassRecordKind ?? 'empty',
      progressClassRecordNote: d?.progressClassRecordNote ?? '',
      progressBehaviorNote: d?.progressBehaviorNote ?? '',
      progressRecordMemberGroupId: d?.progressRecordMemberGroupId ?? null,
    };
    return {
      slotIndex: s.slotIndex,
      periodLabel: s.periodLabel,
      templateLabel: s.templateLabel,
      timetableText: s.timetableText,
      timetableColorHex: s.timetableColorHex,
      timetableSlotKind: s.timetableSlotKind,
      calendarOffDayKind: s.calendarOffDayKind ?? null,
      academicYear: s.academicYear,
      termNumber: s.termNumber,
      mondayIso: s.mondayIso,
      weekdayKey: s.weekdayKey,
      dateIso: s.dateIso,
      annualCellKey: s.annualCellKey,
      slideOwner: s.slideOwner,
      slideOwnerCandidates: s.slideOwnerCandidates ?? [],
      slideOpenDbOwner: s.slideOpenDbOwner,
      slideWorkspaceScopeId: s.slideWorkspaceScopeId ?? '',
      slideLearningTextRaw: s.slideLearningTextRaw ?? '',
      slideCreatePresentationRefOnSave: s.slideCreatePresentationRefOnSave,
      display,
    };
  });
}

function postBroadcast(snapshot: DashboardLessonDaySlotsSnapshotV2): void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage(snapshot);
    ch.close();
  } catch {
    /* ignore */
  }
}

/**
 * 대시보드가 화면에 그린 교시 블럭을 메인 프레젠으로 전달.
 * needsReload/schoolDataCacheVersion 게이트 없음.
 */
export function saveDashboardLessonDaySlotsSnapshot(input: {
  workspaceId: string;
  gradeId: string;
  dateIso: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
  academicYear: number;
  activeTermNumber: 1 | 2 | null;
  daySlots: DashboardAnnualDaySlot[];
}): boolean {
  const workspaceId = input.workspaceId.trim();
  const gradeId = input.gradeId.trim();
  const dateIso = input.dateIso.trim();
  if (!workspaceId || !gradeId || !dateIso || typeof window === 'undefined') return false;
  if (!input.daySlots.length) return false;

  const payload: DashboardLessonDaySlotsSnapshotV2 = {
    v: 2,
    workspaceId,
    gradeId,
    dateIso,
    mondayIso: input.mondayIso,
    weekdayKey: input.weekdayKey,
    academicYear: input.academicYear,
    activeTermNumber: input.activeTermNumber,
    daySlots: slimDashboardDaySlotsForPresentation(input.daySlots, workspaceId),
    savedAt: Date.now(),
  };

  livePublished = {
    workspaceId,
    gradeId,
    dateIso,
    mondayIso: input.mondayIso,
    weekdayKey: input.weekdayKey,
    academicYear: input.academicYear,
    activeTermNumber: input.activeTermNumber,
    daySlots: input.daySlots,
  };

  const dayStorageKey = dateKey(workspaceId, gradeId, dateIso);
  const latestStorageKey = latestKey(workspaceId, gradeId);

  try {
    pruneOldDateSnapshots(workspaceId, gradeId, dateIso);
    const raw = JSON.stringify(payload);
    const dayOk = localStorageSetItemWithQuotaRecovery(dayStorageKey, raw);
    const latestOk = localStorageSetItemWithQuotaRecovery(latestStorageKey, raw);
    postBroadcast(payload);
    if (dayOk || latestOk) {
      devSnapshotInfo('[dashboardLessonDaySlotsSnapshot] saved', {
        workspaceId,
        gradeId,
        dateIso,
        slots: payload.daySlots.length,
        bytes: raw.length,
        hasLesson: snapshotHasLessonAssignments(payload),
        dayOk,
        latestOk,
      });
      return dayOk || latestOk;
    }
    /* LS 전부 실패해도 BroadcastChannel은 보냄 — 같은 세션 프레젠은 동작 */
    devSnapshotWarn(
      '[dashboardLessonDaySlotsSnapshot] localStorage 저장 불가(용량) — BroadcastChannel만 전달'
    );
    return false;
  } catch (err) {
    devSnapshotWarn('[dashboardLessonDaySlotsSnapshot] save failed — BroadcastChannel만 시도', err);
    try {
      postBroadcast(payload);
    } catch {
      /* ignore */
    }
    return false;
  }
}

type LivePublishedDaySlots = {
  workspaceId: string;
  gradeId: string;
  dateIso: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
  academicYear: number;
  activeTermNumber: 1 | 2 | null;
  daySlots: DashboardAnnualDaySlot[];
};

/** 대시보드 훅이 마지막으로 계산한 daySlots (같은 탭 메모리) */
let livePublished: LivePublishedDaySlots | null = null;

/** 메인 탭 responder가 grades를 알 수 있게 */
let responderGrades: Grade[] = [];

export function flushLiveDashboardLessonDaySlotsSnapshot(preferred?: {
  workspaceId?: string;
  gradeId?: string;
  dateIso?: string;
}): boolean {
  const live = livePublished;
  if (!live || live.daySlots.length === 0) {
    return false;
  }
  if (preferred?.workspaceId && live.workspaceId !== preferred.workspaceId.trim()) return false;
  const preferredDate = (preferred?.dateIso ?? '').trim();
  /* 탐색 중이던 날짜를 다른 dateIso 키로 덮어쓰지 않음 (날짜 불일치 remapping 금지) */
  if (preferredDate && live.dateIso !== preferredDate) {
    devSnapshotInfo('[dashboardLessonDaySlotsSnapshot] flush 스킵: live 날짜 불일치', {
      live: live.dateIso,
      preferred: preferredDate,
    });
    return false;
  }
  if (preferred?.gradeId && live.gradeId !== preferred.gradeId.trim()) {
    /* grade만 다르면 요청 grade로 키잉하되 daySlots 내용은 live 그대로(같은 날짜일 때만 도달) */
  }
  const gradeId = (preferred?.gradeId ?? live.gradeId).trim() || live.gradeId;
  return saveDashboardLessonDaySlotsSnapshot({
    ...live,
    gradeId,
    dateIso: live.dateIso,
  });
}

/**
 * 메인 탭의 React Query 메모리(+LS 폴백)에서 대시보드와 동일하게 daySlots를 조립.
 * 새 탭은 RQ 힙을 공유하지 않으므로, 열기 전·요청 응답 시 이 함수로 스냅샷을 만든다.
 */
export function buildDaySlotsFromMainTabQueryClient(input: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  dateIso: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
}): {
  daySlots: DashboardAnnualDaySlot[];
  academicYear: number;
  termNumber: 1 | 2;
  source: string;
} | null {
  const workspaceId = input.workspaceId.trim();
  const gradeId = input.gradeId.trim();
  if (!workspaceId || !gradeId) return null;

  const hintYear = inferAcademicYearForViewDate(input.dateIso);
  const metaHit = readAnnualProgressMetaCacheOrQueryWithFallback(workspaceId, hintYear, gradeId);
  const schedule =
    metaHit.data.schedule ?? loadClassScheduleSettingsCache(workspaceId) ?? null;

  const preferredTerm: 1 | 2 =
    metaHit.data.terms.length > 0
      ? resolveTermNumberForViewDate(metaHit.data.terms, input.dateIso)
      : (metaHit.termNumber as 1 | 2);

  const cellsHit = findAnnualTimetableCellsCacheOrQueryForGrade(workspaceId, gradeId, {
    academicYear: metaHit.found ? metaHit.academicYear : hintYear,
    termNumber: preferredTerm,
  });

  if (!cellsHit.found || cellsHit.rows.length === 0) {
    devSnapshotWarn('[dashboardLessonDaySlotsSnapshot] RQ/LS timetable cells 없음', {
      workspaceId,
      gradeId,
      hintYear,
      preferredTerm,
      metaFound: metaHit.found,
      rqKeys: queryClient
        .getQueriesData({ queryKey: ['school', workspaceId, 'annualTimetableCells'] })
        .map(([k, d]) => ({ key: k, rows: Array.isArray(d) ? d.length : 0 })),
    });
    return null;
  }

  const academicYear = cellsHit.academicYear;
  const termNumber = cellsHit.termNumber;

  const progressFromRq = queryClient.getQueryData<ProgramProgressCell[]>(
    schoolKeys.programProgressCells(workspaceId, academicYear, termNumber, gradeId)
  );
  const progressRows =
    progressFromRq ??
    loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId).rows;

  const scopedRows = readProgramScopedRowsCacheOrQuery(
    workspaceId,
    academicYear,
    termNumber,
    gradeId
  ).rows;
  const fixedRows = readProgramFixedRowsCacheOrQuery(
    workspaceId,
    academicYear,
    termNumber,
    gradeId
  ).rows;

  const projectPrograms =
    queryClient.getQueryData(
      schoolKeys.annualProjectPrograms(workspaceId, academicYear, gradeId)
    ) ?? loadAnnualProjectPrograms(workspaceId, gradeId, academicYear);

  const gradeTrackSections =
    queryClient.getQueryData(schoolKeys.gradeTrackSections(workspaceId)) ??
    loadGradeTrackSectionsCache(workspaceId) ??
    [];

  const activeTermData = withAcademicCalendarOffDaysFromLocalCache<DashboardLessonSectionTermData>(
    {
      termNumber,
      annualRows: cellsHit.rows,
      progressRows,
      scopedRows,
      fixedRows,
      holidayDays: [],
      offDayByDate: {},
      dayKindByDate: {},
    },
    workspaceId,
    gradeId
  );

  const bundle: DashboardLessonSectionBundle = {
    academicYear,
    terms: metaHit.data.terms,
    schedule,
    tracks: metaHit.data.tracks,
    regularColors: metaHit.data.regularColors,
    dedicatedColors: metaHit.data.dedicatedColors,
    baseExcludeColors: metaHit.data.baseExcludeColors,
    annualProjectPrograms: Array.isArray(projectPrograms) ? projectPrograms : [],
    gradeTrackSections: Array.isArray(gradeTrackSections) ? gradeTrackSections : [],
    byTerm: {
      1: termNumber === 1 ? activeTermData : emptyTermData(1),
      2: termNumber === 2 ? activeTermData : emptyTermData(2),
    },
  };

  const grades =
    input.grades.some((g) => g.id === gradeId)
      ? input.grades
      : [
          ...input.grades,
          { id: gradeId, workspace_id: workspaceId, name: '', sort_order: 0 },
        ];

  const daySlots = buildDashboardAnnualDaySlotsFromLoadedData({
    workspaceId,
    gradeId,
    grades,
    mondayIso: input.mondayIso,
    weekdayKey: input.weekdayKey,
    dateIso: input.dateIso,
    bundle,
    termNumber,
  });

  devSnapshotInfo('[dashboardLessonDaySlotsSnapshot] RQ 메모리에서 daySlots 조립', {
    workspaceId,
    gradeId,
    academicYear,
    termNumber,
    annualRows: cellsHit.rows.length,
    progressRows: progressRows.length,
    daySlots: daySlots.length,
    hasLesson: daySlotsHaveLessonAssignments(daySlots),
  });

  return {
    daySlots,
    academicYear,
    termNumber,
    source: 'queryClient',
  };
}

function parseSnapshot(raw: string | null): DashboardLessonDaySlotsSnapshotV2 | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DashboardLessonDaySlotsSnapshotV2 | null;
    if (!parsed || parsed.v !== 2) return null;
    if (!Array.isArray(parsed.daySlots) || parsed.daySlots.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function loadDashboardLessonDaySlotsSnapshot(
  workspaceId: string,
  gradeId: string,
  dateIso: string
): DashboardLessonDaySlotsSnapshotV2 | null {
  const sid = workspaceId.trim();
  const gid = gradeId.trim();
  const day = dateIso.trim();
  if (!sid || !gid || !day || typeof window === 'undefined') return null;
  return parseSnapshot(window.localStorage.getItem(dateKey(sid, gid, day)));
}

export function loadLatestDashboardLessonDaySlotsSnapshot(
  workspaceId: string,
  gradeId: string
): DashboardLessonDaySlotsSnapshotV2 | null {
  const sid = workspaceId.trim();
  const gid = gradeId.trim();
  if (!sid || !gid || typeof window === 'undefined') return null;
  return parseSnapshot(window.localStorage.getItem(latestKey(sid, gid)));
}

function snapshotMatchesRequestedDate(
  snap: DashboardLessonDaySlotsSnapshotV2,
  dateIso: string
): boolean {
  const day = dateIso.trim();
  if (!day || snap.dateIso !== day) return false;
  /* remapping 버그로 키가 오늘인데 슬롯은 다른 날짜인 경우 거부 */
  for (const s of snap.daySlots) {
    if ((s.dateIso ?? '').trim() && s.dateIso.trim() !== day) return false;
  }
  return true;
}

export function resolveDashboardLessonDaySlotsSnapshotForPresentation(
  workspaceId: string,
  gradeId: string,
  dateIso: string
): DashboardLessonDaySlotsSnapshotV2 | null {
  const day = dateIso.trim();
  const byDate = loadDashboardLessonDaySlotsSnapshot(workspaceId, gradeId, day);
  if (byDate && snapshotMatchesRequestedDate(byDate, day)) return byDate;

  const latest = loadLatestDashboardLessonDaySlotsSnapshot(workspaceId, gradeId);
  if (latest && snapshotMatchesRequestedDate(latest, day)) return latest;

  const sid = workspaceId.trim();
  if (!sid || !day || typeof window === 'undefined') return null;
  const prefix = `${SNAPSHOT_PREFIX}.${sid}.`;
  const needle = `.${day}`;
  let best: DashboardLessonDaySlotsSnapshotV2 | null = null;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(prefix) || !key.endsWith(needle)) continue;
      if (key.endsWith('.latest')) continue;
      const hit = parseSnapshot(window.localStorage.getItem(key));
      if (!hit || !snapshotMatchesRequestedDate(hit, day)) continue;
      if (!best || hit.savedAt > best.savedAt) best = hit;
    }
  } catch {
    return best;
  }
  return best;
}

export function subscribeDashboardLessonDaySlotsSnapshot(
  onSnapshot: (snapshot: DashboardLessonDaySlotsSnapshotV2) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(CHANNEL_NAME);
    ch.onmessage = (ev: MessageEvent<DashboardLessonDaySlotsSnapshotV2>) => {
      const data = ev.data;
      if (data?.v === 2 && Array.isArray(data.daySlots) && data.daySlots.length > 0) {
        onSnapshot(data);
      }
    };
  } catch {
    ch = null;
  }

  const onStorage = (e: StorageEvent) => {
    if (!e.key?.startsWith(`${SNAPSHOT_PREFIX}.`) || !e.newValue) return;
    const hit = parseSnapshot(e.newValue);
    if (hit) onSnapshot(hit);
  };
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener('storage', onStorage);
    try {
      ch?.close();
    } catch {
      /* ignore */
    }
  };
}

/** 프레젠 탭 → 메인 탭: 스냅샷 재전송 요청 */
export function requestDashboardLessonDaySlotsSnapshotFromMainTab(input: {
  workspaceId: string;
  gradeId: string;
  dateIso: string;
}): void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(REQUEST_CHANNEL_NAME);
    const msg: SnapshotRequestMessage = {
      type: 'request',
      workspaceId: input.workspaceId.trim(),
      gradeId: input.gradeId.trim(),
      dateIso: input.dateIso.trim(),
    };
    ch.postMessage(msg);
    ch.close();
    devSnapshotInfo('[메인프레젠·캐시] 메인 탭에 스냅샷 요청', msg);
  } catch (err) {
    devSnapshotWarn('[메인프레젠·캐시] 스냅샷 요청 실패', err);
  }
}

/**
 * 메인 앱 탭에서 상시 구독: 프레젠 탭의 request에 대해
 * live daySlots 또는 queryClient 메모리로 스냅샷을 만들어 응답.
 */
export function installMainTabPresentationSnapshotResponder(grades: Grade[]): () => void {
  responderGrades = grades;
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }

  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(REQUEST_CHANNEL_NAME);
    ch.onmessage = (ev: MessageEvent<SnapshotRequestMessage>) => {
      const msg = ev.data;
      if (!msg || msg.type !== 'request') return;
      const workspaceId = (msg.workspaceId ?? '').trim();
      const gradeId = (msg.gradeId ?? '').trim();
      const dateIso = (msg.dateIso ?? '').trim();
      if (!workspaceId || !gradeId || !dateIso) return;

      devSnapshotInfo('[dashboardLessonDaySlotsSnapshot] 프레젠 요청 수신 → RQ/live로 응답', {
        workspaceId,
        gradeId,
        dateIso,
      });

      const result = warmDashboardLessonDaySlotsSnapshotForOpen({
        workspaceId,
        gradeId,
        grades: responderGrades,
        viewDate: (() => {
          const [y, m, d] = dateIso.split('-').map(Number);
          return new Date(y, m - 1, d);
        })(),
      });
      devSnapshotInfo('[dashboardLessonDaySlotsSnapshot] 요청 응답 워밍 결과', result);
    };
  } catch {
    ch = null;
  }

  return () => {
    try {
      ch?.close();
    } catch {
      /* ignore */
    }
  };
}

export function isDashboardLessonDaySlotsSnapshotStorageKey(key: string | null | undefined): boolean {
  return !!key && key.startsWith(`${SNAPSHOT_PREFIX}.`);
}

export function snapshotHasLessonAssignments(snapshot: DashboardLessonDaySlotsSnapshotV2 | null): boolean {
  if (!snapshot?.daySlots?.length) return false;
  return snapshot.daySlots.some((s) =>
    annualSlotHasTimetableProgramAssignment(s.timetableText, s.timetableColorHex)
  );
}

export function daySlotsHaveLessonAssignments(slots: DashboardAnnualDaySlot[]): boolean {
  return slots.some((s) =>
    annualSlotHasTimetableProgramAssignment(s.timetableText, s.timetableColorHex)
  );
}

/**
 * 메인 프레젠 열기 직전(메인 탭) / 프레젠 요청 응답:
 * 요청 dateIso에 대해 연간진도 RQ/LS로 조립하는 것을 정본으로 한다.
 * live 스냅샷은 날짜가 일치할 때만 사용(탐색일 remapping 금지).
 * 수업 배정이 없어도 교시 shell이 있으면 “확인된 빈 날”로 성공 처리.
 */
export function warmDashboardLessonDaySlotsSnapshotForOpen(input: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  viewDate?: Date;
}): { ok: boolean; reason: string; slots: number; hasLesson: boolean } {
  const workspaceId = input.workspaceId.trim();
  const gradeId = input.gradeId.trim();
  if (!workspaceId || !gradeId) {
    return { ok: false, reason: 'workspace/grade 없음', slots: 0, hasLesson: false };
  }

  const viewDate = input.viewDate ?? new Date();
  const dateIso = toISODate(viewDate);
  const weekdayKey = dateToWeekdayKey(viewDate);
  const mondayIso = mondayIsoForDateInLocal(viewDate);

  if (weekdayKey == null) {
    return { ok: false, reason: '주말', slots: 0, hasLesson: false };
  }

  const finish = (
    daySlots: DashboardAnnualDaySlot[],
    academicYear: number,
    termNumber: 1 | 2 | null,
    reason: string
  ): { ok: boolean; reason: string; slots: number; hasLesson: boolean } => {
    if (daySlots.length === 0) {
      return { ok: false, reason, slots: 0, hasLesson: false };
    }
    saveDashboardLessonDaySlotsSnapshot({
      workspaceId,
      gradeId,
      dateIso,
      mondayIso,
      weekdayKey,
      academicYear,
      activeTermNumber: termNumber,
      daySlots,
    });
    return {
      ok: true,
      reason,
      slots: daySlots.length,
      hasLesson: daySlotsHaveLessonAssignments(daySlots),
    };
  };

  /* 1) 대시보드 live — 날짜 일치할 때만 */
  if (
    livePublished &&
    livePublished.workspaceId === workspaceId &&
    livePublished.gradeId === gradeId &&
    livePublished.dateIso === dateIso &&
    livePublished.daySlots.length > 0
  ) {
    flushLiveDashboardLessonDaySlotsSnapshot({ workspaceId, gradeId, dateIso });
    return {
      ok: true,
      reason: 'flush-live',
      slots: livePublished.daySlots.length,
      hasLesson: daySlotsHaveLessonAssignments(livePublished.daySlots),
    };
  }

  /* 2) 연간진도 RQ 메모리(+LS)에서 요청 날짜로 조립 — 정본 */
  const fromRq = buildDaySlotsFromMainTabQueryClient({
    workspaceId,
    gradeId,
    grades: input.grades,
    dateIso,
    mondayIso,
    weekdayKey,
  });
  if (fromRq && fromRq.daySlots.length > 0) {
    return finish(fromRq.daySlots, fromRq.academicYear, fromRq.termNumber, 'queryClient');
  }

  /* 3) 이미 날짜 일치하는 LS 스냅샷 */
  const existing = resolveDashboardLessonDaySlotsSnapshotForPresentation(workspaceId, gradeId, dateIso);
  if (existing && existing.daySlots.length > 0) {
    return {
      ok: true,
      reason: 'existing',
      slots: existing.daySlots.length,
      hasLesson: snapshotHasLessonAssignments(existing),
    };
  }

  /* 4) 원시 LS 재구성 */
  const built = buildDashboardAnnualDaySlots({
    workspaceId,
    gradeId,
    grades: input.grades,
    mondayIso,
    weekdayKey,
    dateIso,
    programSidebarAcademicYear: null,
  });

  if (built.length > 0) {
    return finish(
      built,
      built[0]?.academicYear ?? viewDate.getFullYear(),
      built[0]?.termNumber ?? null,
      'ls-rebuild'
    );
  }

  devSnapshotWarn('[dashboardLessonDaySlotsSnapshot] warm 실패(조립 불가)', {
    workspaceId,
    gradeId,
    dateIso,
  });
  return { ok: false, reason: '날짜별 시간표 캐시 없음', slots: 0, hasLesson: false };
}

export function presentationSlotsFromSnapshot(
  snapshot: DashboardLessonDaySlotsSnapshotV2
): DashboardAnnualDaySlot[] {
  const mapped: DashboardAnnualDaySlot[] = snapshot.daySlots.map((s) => ({
    slotIndex: s.slotIndex,
    periodLabel: s.periodLabel,
    templateLabel: s.templateLabel,
    timetableText: s.timetableText,
    timetableColorHex: s.timetableColorHex,
    timetableSlotKind: s.timetableSlotKind ?? 'class',
    calendarOffDayKind: s.calendarOffDayKind ?? null,
    programKindLabel: '',
    display: s.display,
    academicYear: s.academicYear,
    termNumber: s.termNumber,
    mondayIso: s.mondayIso,
    weekdayKey: s.weekdayKey,
    dateIso: s.dateIso,
    annualCellKey: s.annualCellKey,
    progressCellId: null,
    recordClassTextRaw: '',
    scopedContentRowId: null,
    scopedContentScopeType: null,
    scopedContentScopeKey: null,
    scopedContentOrderIndex: null,
    scopedContentTemplateRowId: null,
    fixedContentRowId: null,
    fixedContentEventName: null,
    fixedContentOrderIndex: null,
    slideOwner: s.slideOwner,
    slideOwnerCandidates: s.slideOwnerCandidates,
    slideOpenDbOwner: s.slideOpenDbOwner,
    slideWorkspaceScopeId: s.slideWorkspaceScopeId,
    slideLearningTextRaw: s.slideLearningTextRaw,
    slideCreatePresentationRefOnSave: s.slideCreatePresentationRefOnSave,
  }));
  return applyProgramContentSlideLearningOverlayToSlots(mapped, snapshot.workspaceId);
}
